import React from 'react';
import Input from '../inputs/inputs';
import Icon from '../icon/icon';
import { useTranslation } from 'react-i18next';
import './counter.css';

export default function Counter({
	min = 0,
	max = 10000,
	onChange = () => {},
	step = 1,
	value = null,
	disabledPlus = false,
	disabledMinus = false,
	numberDecimals = 0,
	ariaLabelPlus = 'widget.counter.add',
	ariaLabelMinus = 'widget.counter.delete',
	ariaLabelInput = 'widget.counter.selected_quantity',
	ariaLabelAnnouncer = 'widget.counter.selected_quantity',
	...rest
}) {
	const { t } = useTranslation();
	const increment = React.useCallback(
		event => {
			const previousValue = value || 0;
			// We don't increment if the max is reached up
			if (previousValue >= max) {
				return;
			}

			const newValue = Number(+previousValue + step);
			if (onChange) {
				onChange(event, newValue);
			}
		},
		[max, onChange, step, value]
	);

	const decrement = React.useCallback(
		event => {
			const previousValue = value || 0;

			// We don't decrement if the min is reached up
			if (previousValue <= min) {
				return;
			}
			const newValue = Number(+previousValue - step);
			if (onChange) {
				// Pass true as third parameter to indicate this change came from decrement button
				onChange(event, newValue, true);
			}
		},
		[min, onChange, step, value]
	);

	const checkHigherOrLowerValue = React.useCallback(
		value => {
			let newValue = +value;

			if (!Number.isInteger(newValue * 10 ** numberDecimals)) {
				newValue =
					Math.trunc(newValue * 10 ** numberDecimals) /
					10 ** numberDecimals;
			}

			// if the new value exceeds the maximum set max
			if (value >= max) {
				newValue = max;
			}

			// if the new value exceeds the minimum set min
			if (value <= min) {
				newValue = min;
			}

			return +newValue;
		},
		[min, max, numberDecimals]
	);

    const toggleChange = React.useCallback(
		event => {
			const newCounterValue = checkHigherOrLowerValue(event.target.value);

			if (onChange) {
				onChange(event, newCounterValue);
			}
		},
		[onChange, checkHigherOrLowerValue]
	);

    const blockChange = React.useCallback(
		event => {
			if (
				numberDecimals === 0 &&
				(event.keyCode === 110 ||
					event.keyCode === 188 ||
					event.keyCode === 190)
			) {
				event.preventDefault();
				event.stopPropagation();
			}
		},
		[numberDecimals]
	);

    return (
		<div className='neo-counter'>
			<div className='neo-counter-div-aria-live' aria-live="assertive">	
				{t(ariaLabelAnnouncer)}
			</div>
            <button
				type='button'
				aria-label={t(ariaLabelMinus)}
				aria-disabled={String((disabledMinus || value === 0 || value <= min))}
				className={`neo-counter-icon-container ${(disabledMinus || value === 0 || value <= min) ? 'neo-counter-icon-container-disabled' : ''}`}
				onClick={decrement}
			>
                <Icon name='minus'/>
            </button>
            <Input
                type='number'
				aria-label={t(ariaLabelInput)}
                value={value ? value.toFixed(numberDecimals) : 0}
                className='neo-counter-input'
                onChange={toggleChange}
                onKeyDown={blockChange}
                onKeyPress={blockChange}   
                disabled={disabledPlus}
                {...rest}
            />
            <button
				type='button'
				aria-label={t(ariaLabelPlus)}
				aria-disabled={String((disabledPlus || value >= max))}
				className={`neo-counter-icon-container ${(disabledPlus || value >= max) ? 'neo-counter-icon-container-disabled' : ''}`}
				onClick={increment}
			>
                <Icon name='plus'/>
            </button>
        </div>
    )
}